/**
 * Quais blocos uma seleção de voos vira.
 *
 * Lógica pura. Sem React, sem fetch, sem estado.
 *
 * DUAS FORMAS DE OFERTA, UMA SÓ MONTAGEM
 * ──────────────────────────────────────
 * A Moblix devolve dois formatos diferentes conforme a busca:
 *
 *   SEPARADA  — duas buscas de só ida. Cada oferta tem `outbound` e preço
 *               próprio. É o desenho documentado no CLAUDE.md ("busca
 *               combinada duplicava resultados").
 *
 *   COMBINADA — uma busca com `returnDate`. Cada oferta traz `outbound` E
 *               `return` no MESMO objeto, com UM preço cobrindo os dois
 *               trechos (`_map_offer` em public_flights.py).
 *
 * O carrinho (`{outbound: [], return: []}`) foi feito para a primeira. Com o
 * checkbox "Ida e volta" marcado, a busca vira combinada e a classificação
 * de `handleAddToCart` nunca acerta: ela testa se a oferta PARTE do destino,
 * e numa oferta combinada o `outbound` sempre parte da origem. Resultado:
 * `cart.return` ficava vazio, o botão travava em "Ver voo de volta", e o
 * vendedor desmarcava o checkbox para destravar — montando cotação só de
 * ida, com a volta descartada em silêncio apesar de estar ali no objeto.
 *
 * A decisão de qual trecho vira bloco mora aqui, num lugar só, e cobre as
 * duas formas.
 *
 * PREÇO — por que a volta vai com zero
 * ────────────────────────────────────
 * `Quote._recalculate_total` (quote.py) faz `sum(b.price)`. Numa oferta
 * combinada o `price.total` já é de ida e volta; copiá-lo nos dois blocos
 * dobraria o valor da cotação.
 *
 * A tarifa de ida e volta é INDIVISÍVEL na origem — a Moblix não devolve o
 * preço por trecho. Qualquer divisão (50/50, proporcional) seria número
 * inventado dentro de um sistema que existe para não inventar dado. Então o
 * total inteiro fica num bloco e o outro vai com 0: a soma bate exatamente
 * com o que a companhia cobra, e nenhum número é fabricado.
 *
 * Na busca separada nada disso se aplica — dois preços reais, dois blocos
 * com o seu.
 */

/**
 * A oferta traz ida e volta no mesmo objeto?
 *
 * `return` com segmentos é o que distingue. Exige `segments` não vazio de
 * propósito: `_map_itinerary` pode devolver estrutura sem trechos, e um
 * bloco de volta sem segmento nenhum seria pior que bloco nenhum.
 */
export function ofertaCombinada(offer) {
  return Array.isArray(offer?.return?.segments) && offer.return.segments.length > 0;
}

/**
 * Plano de montagem: o que virar bloco, em que direção, com ou sem preço.
 *
 * @param {object} offerIda   - oferta do trecho de ida (ou a combinada)
 * @param {object} [offerVolta] - oferta de volta, só na busca SEPARADA
 * @returns {Array<{offer: object, direction: 'ida'|'volta', zerarPreco: boolean}>}
 *
 * Devolve descritores em vez de blocos prontos porque `buildFlightBlock`
 * mora no componente e fecha sobre `adults`/`children`/`infants`. Assim a
 * REGRA fica testável sem arrastar a tela junto.
 *
 * `offerVolta` tem precedência sobre `offerIda.return`: se o chamador passou
 * uma oferta de volta explícita, a busca foi separada e é ela que vale. É o
 * que garante que este módulo não altere aquele fluxo.
 */
export function planoDeBlocos(offerIda, offerVolta) {
  if (!offerIda) return [];

  const ida = { offer: offerIda, direction: 'ida', zerarPreco: false };

  // Busca separada — duas ofertas, dois preços reais.
  if (offerVolta) {
    return [ida, { offer: offerVolta, direction: 'volta', zerarPreco: false }];
  }

  // Busca combinada — uma oferta, um preço. O total já está na ida.
  if (ofertaCombinada(offerIda)) {
    return [ida, { offer: offerIda, direction: 'volta', zerarPreco: true }];
  }

  // Só ida.
  return [ida];
}
